import { Head, Link, router } from "@inertiajs/react"
import { Clock, ArrowRight, Mic } from "lucide-react"
import { AdminLayout } from "../components/AdminLayout"
import { SectionTabs } from "../components/SectionTabs"

const articlesTabs = [
  { label: "All Articles", href: "/app/admin/articles" },
  { label: "Pending Review", href: "/app/admin/articles/pending_review" },
  { label: "Meetings", href: "/app/admin/meetings" },
]
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { PodcastStatusBadge } from "@/components/podcast/PodcastStatusBadge"

function formatDate(dateString) {
  if (!dateString) return "-"
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

function formatTimeSince(dateString) {
  if (!dateString) return ""
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now - date
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))

  if (diffDays > 0) {
    return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`
  }
  if (diffHours > 0) {
    return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`
  }
  return "Just now"
}

export default function PendingReview({ currentUser, articles, pendingCount }) {
  return (
    <AdminLayout currentUser={currentUser} pendingCount={pendingCount}>
      <Head title="Pending Review - Kingdom Factor Admin" />

      <div className="space-y-6">
        <SectionTabs tabs={articlesTabs} />

        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-amber-100">
            <Clock className="h-6 w-6 text-amber-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">Pending Review</h1>
            <p className="text-zinc-500">
              {articles?.length || 0} articles waiting for review
            </p>
          </div>
        </div>

        {/* Articles */}
        {articles?.length > 0 ? (
          <div className="space-y-4">
            {articles.map((article) => (
              <Card
                key={article.id}
                className="bg-white border-zinc-200 hover:border-amber-300 transition-colors"
              >
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className="bg-amber-500 text-white">
                          Pending
                        </Badge>
                        {article.podcastEnabled && (
                          article.podcastStatus ? (
                            <PodcastStatusBadge status={article.podcastStatus} />
                          ) : (
                            <Badge variant="outline" className="border-0 bg-purple-100 text-purple-700">
                              <Mic className="h-3 w-3 mr-1" />
                              Podcast
                            </Badge>
                          )
                        )}
                        <span className="text-sm text-zinc-400">
                          Submitted {formatTimeSince(article.submittedAt)}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-zinc-900">
                        {article.title}
                      </h3>
                      <p className="text-sm text-zinc-500">
                        by{" "}
                        <span className="text-zinc-700">
                          {article.author?.name || article.author?.email}
                        </span>{" "}
                        &middot; {article.category || "No category"}
                      </p>
                      {article.excerpt && (
                        <p className="text-sm text-zinc-400 line-clamp-2">
                          {article.excerpt}
                        </p>
                      )}
                    </div>
                    <Link href={`/app/admin/articles/${article.id}`}>
                      <Button
                        variant="outline"
                        className="border-amber-300 text-amber-700 hover:bg-amber-50 whitespace-nowrap"
                      >
                        Review Article
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="bg-white border-zinc-200">
            <CardContent className="py-12 text-center">
              <div className="p-4 rounded-full bg-green-100 w-fit mx-auto mb-4">
                <Clock className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-zinc-900 mb-2">
                All caught up!
              </h3>
              <p className="text-zinc-500">
                No articles are waiting for review.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  )
}
